import React, { useRef, useState } from 'react';
import { FiDownload, FiTrash2 } from 'react-icons/fi';
import { get_resume_file } from '../../Services/Student/StudentApi';
import { isApiFailure } from '../../utils/applicationProfile';

export default function ProfileResumeUpload({ file, savedFilename, onChange }) {
  const inputRef = useRef(null);
  const downloadingRef = useRef(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const filename = file?.name || savedFilename || '';
  const canDownload = Boolean(file || savedFilename);

  const handleDownload = async () => {
    if (downloadingRef.current || !canDownload) return;
    downloadingRef.current = true;
    setDownloading(true);
    setError('');
    try {
      let blob = file;
      let downloadFilename = filename;
      if (!blob) {
        const response = await get_resume_file();
        if (isApiFailure(response) || !response.resume_file?.content_b64) {
          throw new Error('Resume file unavailable');
        }
        const savedFile = response.resume_file;
        downloadFilename = savedFile.filename || filename;
        const content = savedFile.content_b64.replace(/^data:[^;]+;base64,/, '').replace(/\s/g, '');
        const bytes = Uint8Array.from(window.atob(content), character => character.charCodeAt(0));
        blob = new Blob([bytes], { type: savedFile.mimetype || 'application/octet-stream' });
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFilename || 'Resume';
      document.body.appendChild(link);
      try {
        link.click();
      } finally {
        link.remove();
        // Give the browser time to start reading the file before releasing it.
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch {
      setError('Unable to download this resume. Please try again.');
    } finally {
      downloadingRef.current = false;
      setDownloading(false);
    }
  };

  return (
    <div className="profile-resume-control">
      <div className="resume-upload-wrapper">
        <button type="button" className="resume-upload-btn" disabled={downloading} onClick={() => inputRef.current.click()}>
          Upload New
        </button>
        <span className="resume-file-name" title={filename}>{filename || 'No resume uploaded'}</span>
        <div className="resume-file-actions">
          <button
            type="button"
            className="resume-download-btn"
            onClick={handleDownload}
            disabled={!canDownload || downloading}
            aria-busy={downloading}
            aria-label={file ? 'Download selected resume' : 'Download saved resume'}
            title={!canDownload ? 'Upload a resume to download it' : `Download ${filename}`}
          >
            <FiDownload aria-hidden="true" />
            <span>{downloading ? 'Downloading…' : 'Download'}</span>
          </button>
          {file && (
            <button
              type="button"
              className="resume-remove-btn"
              disabled={downloading}
              aria-label="Remove selected resume"
              title="Remove selected resume"
              onClick={() => {
                inputRef.current.value = '';
                setError('');
                onChange(null);
              }}
            >
              <FiTrash2 aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        id="resumeUploadInput"
        aria-label="Upload new resume"
        accept=".pdf,.docx,.txt"
        hidden
        onClick={event => { event.target.value = ''; }}
        onChange={event => {
          if (event.target.files[0]) {
            setError('');
            onChange(event.target.files[0]);
          }
        }}
      />
      {file && <p className="resume-selection-note">New resume selected. Save your profile to upload it.</p>}
      {error && <p className="resume-download-error" role="alert">{error}</p>}
    </div>
  );
}
